import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { CheckCircle, Sparkle, Bell, ArrowRight, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0, recovering: false });
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const poll = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }
    if (attempts > 8) {
      setState({ status: "timeout" });
      return;
    }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid" || data.granted) {
        setState({ status: "paid", data });
        refresh();
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired" });
        return;
      }
      setState({ status: "pending", attempts });
      setTimeout(() => poll(attempts + 1), 2000);
    } catch (e) {
      setState({ status: "error", message: e.response?.data?.detail || "Status check failed" });
    }
  }, [sessionId, refresh]);

  // ------------------------------------------------------------------
  // Self-recovery: re-verify the Stripe session and grant Pro if paid.
  // Useful when the webhook fires before the local checkout row exists
  // or when the /status poll times out for unrelated reasons.
  // ------------------------------------------------------------------
  async function recover() {
    const sid = sessionId;
    if (!sid) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }
    setState({ status: "pending", recovering: true });
    try {
      const { data } = await api.post(`/billing/recover/${sid}`);
      if (data.granted) {
        setState({ status: "paid", data: { pro_until: data.pro_until } });
        refresh();
      } else {
        setState({ status: "timeout", message: data.note || "Payment still pending at Stripe." });
      }
    } catch (e) {
      setState({ status: "error", message: e.response?.data?.detail || "Recovery failed. Contact support." });
    }
  }

  useEffect(() => {
    poll(0);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-sand-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="billing-success-page">
        {state.status === "checking" && (
          <>
            <Sparkle size={56} weight="duotone" className="mx-auto text-ochre animate-pulse-soft" />
            <h1 className="font-display text-4xl text-ink mt-4">Confirming payment…</h1>
            <p className="text-mute mt-2">Hang tight while we check with Stripe.</p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Sparkle size={56} weight="duotone" className="mx-auto text-ochre animate-pulse-soft" />
            <h1 className="font-display text-4xl text-ink mt-4">Almost there…</h1>
            <p className="text-mute mt-2">Stripe is finalizing the transaction. This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle size={72} weight="duotone" className="mx-auto text-forest" />
            <h1 className="font-display text-5xl text-ink mt-4">Welcome to Grower.</h1>
            <p className="text-mute mt-2">
              Pro is active{state.data?.pro_until && <> until <span className="font-mono-data">{new Date(state.data.pro_until).toLocaleDateString()}</span></>}. Climate-stress alerts are live for your plant collection.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/plants" className="btn-primary" data-testid="success-plants"><Bell size={14} weight="bold" /> Set up alerts</Link>
              <Link to="/scan" className="btn-secondary">New scan <ArrowRight size={14} /></Link>
            </div>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Sparkle size={56} weight="duotone" className="mx-auto text-mute" />
            <h1 className="font-display text-4xl text-ink mt-4">Still confirming…</h1>
            <p className="text-mute mt-2">
              {state.message || "Stripe is taking longer than usual. If your card was charged, click Recover access — we&apos;ll re-verify with Stripe and unlock your Pro plan."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={recover}
                disabled={state.recovering}
                className="btn-primary !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                data-testid="recover-pro-btn"
              >
                <ArrowClockwise size={14} weight="bold" /> {state.recovering ? "Re-checking with Stripe…" : "Recover Pro access"}
              </button>
              <button onClick={() => navigate("/pricing")} className="btn-secondary" data-testid="back-to-pricing">Back to pricing</button>
            </div>
            <p className="text-mute text-xs mt-4 font-mono-data">session_id: <span className="break-all">{sessionId}</span></p>
          </>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <>
            <XCircle size={56} weight="duotone" className="mx-auto text-terra" />
            <h1 className="font-display text-4xl text-ink mt-4">Payment didn&apos;t complete</h1>
            <p className="text-mute mt-2">{state.message || "The Stripe session expired or was cancelled."}</p>
            <Link to="/pricing" className="btn-primary mt-6 inline-flex">Try again</Link>
          </>
        )}
      </main>
    </div>
  );
}
